import React, { useRef, useEffect, useCallback } from 'react';
import '../../assets/styles/before-after-slider.css';

/**
 * Before / After image comparison slider.
 * Drag the handle (or use arrow keys) to wipe between the two images.
 */
const BeforeAfterSlider = ({
  beforeSrc = '/images/before-after-before.jpg',
  afterSrc = '/images/before-after-after.jpg',
  beforeAlt = 'Before',
  afterAlt = 'After',
  beforeLabel = 'Before',
  afterLabel = 'After',
  note = 'Illustrative simulation — individual results vary.'
}) => {
  const sliderRef = useRef(null);
  const handleRef = useRef(null);
  const draggingRef = useRef(false);

  const setPos = useCallback((p) => {
    const slider = sliderRef.current;
    const handle = handleRef.current;
    if (!slider || !handle) return;
    const clamped = Math.max(0, Math.min(100, p));
    slider.style.setProperty('--pos', `${clamped}%`);
    // Fade each label as its side of the image shrinks, so the visible
    // image (before vs after) is always clearly indicated while sliding.
    slider.style.setProperty('--before-op', Math.min(1, clamped / 14).toFixed(3));
    slider.style.setProperty('--after-op', Math.min(1, (100 - clamped) / 14).toFixed(3));
    handle.setAttribute('aria-valuenow', Math.round(clamped));
  }, []);

  const setFromEvent = useCallback((e) => {
    const slider = sliderRef.current;
    if (!slider) return;
    const rect = slider.getBoundingClientRect();
    setPos(((e.clientX - rect.left) / rect.width) * 100);
  }, [setPos]);

  useEffect(() => {
    const slider = sliderRef.current;
    const handle = handleRef.current;
    if (!slider || !handle) return undefined;

    const onPointerDown = (e) => {
      draggingRef.current = true;
      slider.setPointerCapture(e.pointerId);
      setFromEvent(e);
    };
    const onPointerMove = (e) => {
      if (draggingRef.current) setFromEvent(e);
    };
    const onPointerUp = () => { draggingRef.current = false; };
    const onKeyDown = (e) => {
      const current = parseFloat(handle.getAttribute('aria-valuenow')) || 50;
      const step = e.shiftKey ? 10 : 2;
      if (e.key === 'ArrowLeft') { setPos(current - step); e.preventDefault(); }
      if (e.key === 'ArrowRight') { setPos(current + step); e.preventDefault(); }
      if (e.key === 'Home') { setPos(0); e.preventDefault(); }
      if (e.key === 'End') { setPos(100); e.preventDefault(); }
    };

    slider.addEventListener('pointerdown', onPointerDown);
    slider.addEventListener('pointermove', onPointerMove);
    slider.addEventListener('pointerup', onPointerUp);
    slider.addEventListener('pointercancel', onPointerUp);
    handle.addEventListener('keydown', onKeyDown);

    setPos(50);

    return () => {
      slider.removeEventListener('pointerdown', onPointerDown);
      slider.removeEventListener('pointermove', onPointerMove);
      slider.removeEventListener('pointerup', onPointerUp);
      slider.removeEventListener('pointercancel', onPointerUp);
      handle.removeEventListener('keydown', onKeyDown);
    };
  }, [setPos, setFromEvent]);

  return (
    <div className="ba-wrap">
      <div className="ba" ref={sliderRef}>
        <img className="ba__after" alt={afterAlt} src={afterSrc} />
        <img className="ba__before" alt={beforeAlt} src={beforeSrc} />
        <span className="ba__label ba__label--before">{beforeLabel}</span>
        <span className="ba__label ba__label--after">{afterLabel}</span>
        <div className="ba__divider" />
        <div
          className="ba__handle"
          ref={handleRef}
          role="slider"
          tabIndex={0}
          aria-label="Compare before and after"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={50}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 6 4 12 9 18" />
            <polyline points="15 6 20 12 15 18" />
          </svg>
        </div>
      </div>
      {note ? <p className="ba__note">{note}</p> : null}
    </div>
  );
};

export default BeforeAfterSlider;
